import { useEffect, useRef, useState } from 'react';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CampaignIcon from '@mui/icons-material/Campaign';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { NOTIFICATION_TYPE } from 'context/NotificationCenterContext';
import type { AppNotification } from 'context/NotificationCenterContext';
import {
  GENERAL_NOTIFICATION_THEME,
  getFulfillmentNotificationTitle,
  getFulfillmentThemeForNotification,
  getGlobalEventTheme,
  getNotificationTitle,
  getWorkboardTaskNotificationTitle,
  getWorkboardTaskTheme,
  NOTIFICATION_COPY,
  NOTIFICATION_TOAST_DURATION_MS,
} from './NotificationCenter.constants';
import {
  cardInProgressInnerSx,
  cardInProgressSx,
  getNotificationLinkChipSx,
  themedToastSurfaceSx,
  timestampSx,
  viewResultsLinkSx,
} from './NotificationCenter.styles';
import { NOTIFICATION_VARIANT } from './NotificationCenter.types';
import { getNotificationUrl, getNotificationVariant, isValidUrl } from './NotificationCenter.utils';
import {
  getNotificationIcon,
  getNotificationIconCircleSx,
  getNotificationTitleColor,
  getToastOuterSx,
} from './NotificationCenter.variantUi';
import {
  JobNotificationMainText,
  NotificationDismissButton,
  NotificationSubtitle,
  ThemedNotificationBody,
} from './NotificationShared';

interface NotificationToastProps {
  notification: AppNotification;
  onDismiss: (id: string) => void;
}

const NotificationToast = ({ notification, onDismiss }: NotificationToastProps) => {
  const { id } = notification;
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const variant = getNotificationVariant(notification);
  const resultUrl = getNotificationUrl(notification);

  const handleClose = () => {
    setExiting(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setTimeout(() => onDismiss(id), 280);
  };

  useEffect(() => {
    timerRef.current = setTimeout(() => handleClose(), NOTIFICATION_TOAST_DURATION_MS);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slideAnimation = {
    animation: exiting
      ? 'toastSlideOut 0.28s cubic-bezier(0.4,0,1,1) forwards'
      : 'toastSlideIn 0.32s cubic-bezier(0,0,0.2,1) forwards',
    '@keyframes toastSlideIn': {
      from: { transform: 'translateX(110%)', opacity: 0 },
      to: { transform: 'translateX(0)', opacity: 1 },
    },
    '@keyframes toastSlideOut': {
      from: { transform: 'translateX(0)', opacity: 1 },
      to: { transform: 'translateX(110%)', opacity: 0 },
    },
  };

  if (notification.type === NOTIFICATION_TYPE.FULFILLMENT) {
    const theme = getFulfillmentThemeForNotification(notification);
    const title = getFulfillmentNotificationTitle(notification);
    return (
      <Box role="presentation" sx={{ ...themedToastSurfaceSx(theme), ...slideAnimation }}>
        <ThemedNotificationBody
          theme={theme}
          icon={<AssignmentIndIcon sx={{ fontSize: 16, color: theme.iconColor }} />}
          title={title}
          onDismiss={handleClose}>
          {notification.companyName && (
            <NotificationSubtitle>{notification.companyName}</NotificationSubtitle>
          )}
        </ThemedNotificationBody>
      </Box>
    );
  }

  if (notification.type === NOTIFICATION_TYPE.WORKBOARD_TASK) {
    const theme = getWorkboardTaskTheme();
    const title = getWorkboardTaskNotificationTitle(notification);
    return (
      <Box role="presentation" sx={{ ...themedToastSurfaceSx(theme), ...slideAnimation }}>
        <ThemedNotificationBody
          theme={theme}
          icon={<AssignmentIndIcon sx={{ fontSize: 16, color: theme.iconColor }} />}
          title={title}
          onDismiss={handleClose}>
          {notification.taskName && (
            <NotificationSubtitle>{notification.taskName}</NotificationSubtitle>
          )}
        </ThemedNotificationBody>
      </Box>
    );
  }

  if (notification.type === NOTIFICATION_TYPE.GLOBAL || notification.type === NOTIFICATION_TYPE.GENERAL) {
    const theme = notification.type === NOTIFICATION_TYPE.GLOBAL
      ? getGlobalEventTheme(notification.eventType)
      : GENERAL_NOTIFICATION_THEME;
    const AnnouncementIcon = notification.type === NOTIFICATION_TYPE.GLOBAL ? CampaignIcon : NotificationsIcon;
    const linkUrl = notification.link && isValidUrl(notification.link) ? notification.link : null;
    const linkLabel = linkUrl ? (notification.linkLabel ?? 'View') : null;
    return (
      <Box role="presentation" sx={{ ...themedToastSurfaceSx(theme), ...slideAnimation }}>
        <ThemedNotificationBody
          theme={theme}
          icon={<AnnouncementIcon sx={{ fontSize: 16, color: theme.iconColor }} />}
          title={notification.title}
          onDismiss={handleClose}>
          <NotificationSubtitle>{notification.content}</NotificationSubtitle>
          {linkUrl && (
            <Box sx={{ mt: 0.75 }}>
              <Box
                component="a"
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                sx={getNotificationLinkChipSx(theme)}>
                {linkLabel}
              </Box>
            </Box>
          )}
        </ThemedNotificationBody>
      </Box>
    );
  }

  // After global/general early return, notification is narrowed to JobAppNotification
  const { job } = notification;

  const { PROCESSING, COMPLETED, FAILED } = NOTIFICATION_VARIANT;

  const outerSx = getToastOuterSx(variant);
  const iconCircle = getNotificationIconCircleSx(variant);
  const icon = getNotificationIcon(variant);
  const title = getNotificationTitle(variant);
  const titleColor = getNotificationTitleColor(variant);

  const inner = (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 1, alignItems: 'flex-start' }}>
      <Box sx={iconCircle}>{icon}</Box>
      <JobNotificationMainText
        variant={variant}
        job={job}
        title={title}
        titleColor={titleColor}
        timestamp={
          (variant === COMPLETED || variant === FAILED) ? (
            <Typography sx={timestampSx}>{NOTIFICATION_COPY.time.justNow}</Typography>
          ) : null
        }
        resultsExtra={
          resultUrl ? (
            <Typography sx={viewResultsLinkSx}>
              {NOTIFICATION_COPY.actions.viewResults}
            </Typography>
          ) : null
        }
      />
      <NotificationDismissButton onClick={handleClose} />
    </Box>
  );

  if (variant === PROCESSING) {
    return (
      <Box
        role="presentation"
        sx={{
          ...cardInProgressSx,
          width: 360,
          boxShadow: '0px 8px 32px rgba(0,0,0,0.13)',
          ...slideAnimation,
          cursor: 'default',
        }}>
        <Box sx={cardInProgressInnerSx}>{inner}</Box>
      </Box>
    );
  }

  const content = (
    <Box sx={{ ...outerSx, p: 0, width: 360, backgroundColor: '#fff', boxShadow: '0px 8px 32px rgba(0,0,0,0.13)', ...slideAnimation }}>
      <Box sx={{ position: 'relative', zIndex: 1, p: 1.5 }}>{inner}</Box>
    </Box>
  );

  if (resultUrl) {
    return (
      <Link
        to={resultUrl}
        onClick={() => {
          handleClose();
        }}
        style={{ textDecoration: 'none', display: 'block', cursor: 'pointer' }}>
        {content}
      </Link>
    );
  }

  return <Box sx={{ cursor: 'default' }}>{content}</Box>;
};

export default NotificationToast;
